import { AppRoute } from '@agentselly/immodossier-shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { useAppParams } from '~/helper/useAppParams';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useV3Redesign } from '~/helper/useV3Redesign';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_INDEX,
)({
  component: Body,
});

const DossierIcon = () => (
  <svg
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
);

const CockpitIcon = () => (
  <svg
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
    />
  </svg>
);

const ArrowIcon = () => (
  <svg
    className="h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500 dark:text-gray-400 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary-600 motion-reduce:transition-none"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 5l7 7-7 7"
    />
  </svg>
);

function Body() {
  const { t } = useTranslate();
  const params = useAppParams(Route);
  const v3 = useV3Redesign();
  const query = useLoaderQuery(AppRoute.ROOT, {
    currentRoute: Route.id,
    params: params as any,
  });

  const showDossier = Boolean(query.data?.showDemoDossier);
  const showCockpit = Boolean(query.data?.showDemoCockpit);

  // If the user has explicitly lost access to everything (or hasn't loaded yet),
  // we fallback to rendering both so they can at least try, or see an auth wall.
  const links = [];
  if (showDossier || (!showDossier && !showCockpit)) {
    links.push({
      label: 'demo_link_dossier',
      route: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_DOSSIER_HOME,
      Icon: DossierIcon,
    });
  }
  if (showCockpit || (!showDossier && !showCockpit)) {
    links.push({
      label: 'demo_link_cockpit',
      route: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_COCKPIT_HOME,
      Icon: CockpitIcon,
    });
  }

  if (v3) {
    return (
      <div className="v3p-page">
        <div
          className="v3-head"
          data-od-id="page-head"
        >
          <h1>{t('v3_demo_title', 'ImmoDossier ausprobieren')}</h1>
          <p>
            {t(
              'v3_demo_subtitle',
              'Schauen Sie sich Dossier und ImmoCockpit mit Beispieldaten an.',
            )}
          </p>
        </div>
        <div className="v3p-grid split">
          {links.map(({ label, route }) => (
            <Link
              key={label}
              to={route as any}
              params={{ ...params } as any}
              className="v3-card"
              style={{ textDecoration: 'none', color: 'inherit' }}
              data-od-id={
                label === 'demo_link_dossier'
                  ? 'demo-Dossier'
                  : 'demo-ImmoCockpit'
              }
            >
              <h2>{t(label)}</h2>
              <p className="v3p-mu v3p-sm">
                {label === 'demo_link_dossier'
                  ? t(
                      'v3_demo_dossier_desc',
                      'Dashboard, Bewertung, Region und Markt mit Beispielobjekt',
                    )
                  : t(
                      'v3_demo_cockpit_desc',
                      'Dashboard, Interessenten, Angebote, Kalender, Aktivitäten, Statistik, Dokumente',
                    )}
              </p>
              <span className="v3p-mu v3p-xs v3p-num">
                {label === 'demo_link_dossier'
                  ? '/demo/dossier/home'
                  : '/demo/cockpit/home'}
              </span>
            </Link>
          ))}
        </div>
        <section
          className="v3-card"
          data-od-id="demo-diff"
          aria-labelledby="h-df"
        >
          <h2 id="h-df">
            {t('v3_demo_diff_title', 'Was ist in der Demo anders?')}
          </h2>
          <div className="v3p-tbl-wrap">
            <table className="v3p-tbl">
              <thead>
                <tr>
                  <th scope="col">{t('v3_demo_diff_feature', 'Funktion')}</th>
                  <th scope="col">{t('v3_demo_diff_demo', 'Demo')}</th>
                  <th scope="col">{t('v3_demo_diff_account', 'Mit Konto')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t('v3_demo_diff_data', 'Daten')}</td>
                  <td>{t('v3_demo_diff_data_demo', 'Beispielobjekte')}</td>
                  <td>{t('v3_demo_diff_data_acc', 'Ihre Objekte, live')}</td>
                </tr>
                <tr>
                  <td>{t('v3_demo_diff_changes', 'Änderungen')}</td>
                  <td>
                    {t('v3_demo_diff_changes_demo', 'Werden nicht gespeichert')}
                  </td>
                  <td>{t('v3_demo_diff_changes_acc', 'Gespeichert')}</td>
                </tr>
                <tr>
                  <td>{t('v3_demo_diff_contacts', 'Kontakte, Angebote')}</td>
                  <td>{t('v3_demo_diff_contacts_demo', 'Beispiel')}</td>
                  <td>
                    {t('v3_demo_diff_contacts_acc', 'Echte Interessenten')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 p-4 sm:gap-4 md:grid-cols-2 md:p-0 max-w-4xl mx-auto w-full">
      {links.map(({ label, route, Icon }, idx) => (
        <Link
          key={idx}
          to={route as any}
          params={{ ...params } as any}
          className="group flex min-h-[64px] items-center justify-between gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:border-gray-700 p-5 shadow-sm dark:shadow-gray-900/50 transition-all duration-200 hover:border-primary-400 hover:shadow-md dark:hover:shadow-gray-900/50 active:scale-[0.98] motion-reduce:transition-none md:p-6"
        >
          <span className="flex items-center gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 text-gray-400 dark:text-gray-500 dark:text-gray-400 transition-colors duration-200 group-hover:bg-primary-50 group-hover:text-primary-600 motion-reduce:transition-none ring-1 ring-gray-200 dark:ring-gray-700 group-hover:ring-primary-100">
              <Icon />
            </span>
            <span className="text-base font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors md:text-lg">
              {t(label)}
            </span>
          </span>
          <ArrowIcon />
        </Link>
      ))}
    </div>
  );
}
